import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useTheme } from '../context/ThemeContext';
import { enterpriseService } from '../services/enterpriseService';
import {
  Shield, FileText, Download, Search, ExternalLink, CheckCircle,
  Lock, Clock, Building2, Globe, Sun, Moon, ChevronDown, ChevronRight,
  Award, AlertCircle, Eye, Calendar, Mail, User, Send, X, Filter,
  BadgeCheck, FileCheck, Bookmark, Info
} from 'lucide-react';

interface ComplianceDocument {
  id: number;
  title: string;
  description: string;
  category: 'certification' | 'policy' | 'report' | 'attestation' | 'questionnaire';
  framework?: string;
  file_type: 'pdf' | 'xlsx' | 'docx';
  file_size: string;
  last_updated: string;
  requires_nda: boolean;
  is_public: boolean;
}

interface Certification {
  id: number;
  name: string;
  issuer: string;
  issue_date: string;
  expiry_date?: string;
  status: 'active' | 'expired' | 'pending';
  logo_url?: string;
  certificate_url?: string;
}

interface SecurityControl {
  id: number;
  category: string;
  title: string;
  description: string;
  status: 'implemented' | 'in_progress' | 'planned';
}

interface OrganizationProfile {
  name: string;
  logo_url?: string;
  description: string;
  industry: string;
  headquarters: string;
  security_email: string;
  dpo_email?: string;
}

export const PublicTrustCenter: React.FC = () => {
  const { language, setLanguage } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ComplianceDocument | null>(null);
  const [ndaForm, setNdaForm] = useState({ name: '', email: '', company: '', agreed: false });
  const [ndaSubmitted, setNdaSubmitted] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['certifications', 'documents']));
  
  // Data state
  const [organization, setOrganization] = useState<OrganizationProfile | null>(null);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [documents, setDocuments] = useState<ComplianceDocument[]>([]);
  const [securityControls, setSecurityControls] = useState<SecurityControl[]>([]);
  
  // Get organization slug from URL
  const orgSlug = searchParams.get('org') || 'imperion';
  
  useEffect(() => {
    loadTrustCenterData();
  }, [orgSlug]);
  
  const loadTrustCenterData = async () => {
    setIsLoading(true);
    try {
      // In real implementation, fetch from API
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mock organization data
      setOrganization({
        name: 'Imperion Security Solutions',
        description: language === 'sr' 
          ? 'Vodeći provajder GRC rješenja u Jugoistočnoj Evropi'
          : 'Leading GRC solutions provider in Southeast Europe',
        industry: language === 'sr' ? 'Tehnologija / Softver' : 'Technology / Software',
        headquarters: 'Belgrade, Serbia',
        security_email: 'security@imperion.io',
        dpo_email: 'dpo@imperion.io'
      });
      
      // Mock certifications
      setCertifications([
        {
          id: 1,
          name: 'ISO 27001:2022',
          issuer: 'Bureau Veritas',
          issue_date: '2024-06-15',
          expiry_date: '2027-06-14',
          status: 'active'
        },
        {
          id: 2,
          name: 'ISO 9001:2015',
          issuer: 'TÜV SÜD',
          issue_date: '2023-03-20',
          expiry_date: '2026-03-19',
          status: 'active'
        },
        {
          id: 3,
          name: 'SOC 2 Type II',
          issuer: 'Deloitte',
          issue_date: '2025-01-10',
          expiry_date: '2026-01-09',
          status: 'active'
        },
        {
          id: 4,
          name: 'GDPR Compliance',
          issuer: 'EuroPrivacy',
          issue_date: '2024-09-01',
          status: 'active'
        }
      ]);
      
      // Mock documents
      setDocuments([
        {
          id: 1,
          title: 'ISO 27001 Certificate',
          description: language === 'sr' 
            ? 'Trenutni ISO 27001:2022 sertifikat izdat od Bureau Veritas'
            : 'Current ISO 27001:2022 certificate issued by Bureau Veritas',
          category: 'certification',
          framework: 'ISO 27001',
          file_type: 'pdf',
          file_size: '1.2 MB',
          last_updated: '2024-06-15',
          requires_nda: false,
          is_public: true
        },
        {
          id: 2,
          title: 'SOC 2 Type II Report',
          description: language === 'sr' 
            ? 'Godišnji SOC 2 Type II izvještaj o sigurnosnim kontrolama'
            : 'Annual SOC 2 Type II report on security controls',
          category: 'report',
          framework: 'SOC 2',
          file_type: 'pdf',
          file_size: '8.5 MB',
          last_updated: '2025-01-10',
          requires_nda: true,
          is_public: false
        },
        {
          id: 3,
          title: language === 'sr' ? 'Politika privatnosti' : 'Privacy Policy',
          description: language === 'sr' 
            ? 'Naša politika o zaštiti i obradi ličnih podataka'
            : 'Our policy on personal data protection and processing',
          category: 'policy',
          file_type: 'pdf',
          file_size: '450 KB',
          last_updated: '2025-02-01',
          requires_nda: false,
          is_public: true
        },
        {
          id: 4,
          title: language === 'sr' ? 'Politika sigurnosti informacija' : 'Information Security Policy',
          description: language === 'sr' 
            ? 'Pregled naše politike upravljanja sigurnošću informacija'
            : 'Overview of our information security management policy',
          category: 'policy',
          file_type: 'pdf',
          file_size: '680 KB',
          last_updated: '2025-01-15',
          requires_nda: false,
          is_public: true
        },
        {
          id: 5,
          title: 'CAIQ (Consensus Assessment)',
          description: language === 'sr' 
            ? 'Cloud Security Alliance CAIQ upitnik'
            : 'Cloud Security Alliance CAIQ questionnaire',
          category: 'questionnaire',
          framework: 'CSA STAR',
          file_type: 'xlsx',
          file_size: '2.1 MB',
          last_updated: '2024-12-20',
          requires_nda: true,
          is_public: false
        },
        {
          id: 6,
          title: 'Penetration Test Summary',
          description: language === 'sr' 
            ? 'Rezime najnovijeg penetracionog testa (NDA potreban)'
            : 'Summary of latest penetration test results (NDA required)',
          category: 'report',
          file_type: 'pdf',
          file_size: '3.2 MB',
          last_updated: '2025-02-15',
          requires_nda: true,
          is_public: false
        },
        {
          id: 7,
          title: 'Business Continuity Plan Summary',
          description: language === 'sr' 
            ? 'Pregled našeg plana kontinuiteta poslovanja'
            : 'Overview of our business continuity plan',
          category: 'policy',
          file_type: 'pdf',
          file_size: '520 KB',
          last_updated: '2024-11-10',
          requires_nda: true,
          is_public: false
        },
        {
          id: 8,
          title: 'GDPR Data Processing Addendum',
          description: language === 'sr' 
            ? 'Dodatak ugovoru o obradi podataka u skladu sa GDPR'
            : 'Data Processing Addendum compliant with GDPR',
          category: 'attestation',
          framework: 'GDPR',
          file_type: 'docx',
          file_size: '180 KB',
          last_updated: '2024-09-01',
          requires_nda: false,
          is_public: true
        }
      ]);
      
      // Mock security controls
      setSecurityControls([
        {
          id: 1,
          category: language === 'sr' ? 'Kontrola pristupa' : 'Access Control',
          title: language === 'sr' ? 'Multi-faktorska autentifikacija' : 'Multi-Factor Authentication',
          description: language === 'sr' 
            ? 'MFA je obavezan za sve korisnike i sve pristupe sistemima'
            : 'MFA is required for all users and all system access',
          status: 'implemented'
        },
        {
          id: 2,
          category: language === 'sr' ? 'Enkripcija' : 'Encryption',
          title: language === 'sr' ? 'Enkripcija podataka u mirovanju' : 'Data Encryption at Rest',
          description: language === 'sr' 
            ? 'AES-256 enkripcija za sve podatke koji se čuvaju'
            : 'AES-256 encryption for all stored data',
          status: 'implemented'
        },
        {
          id: 3,
          category: language === 'sr' ? 'Enkripcija' : 'Encryption',
          title: language === 'sr' ? 'Enkripcija podataka u prenosu' : 'Data Encryption in Transit',
          description: language === 'sr' 
            ? 'TLS 1.3 za svu mrežnu komunikaciju'
            : 'TLS 1.3 for all network communication',
          status: 'implemented'
        },
        {
          id: 4,
          category: language === 'sr' ? 'Monitoring' : 'Monitoring',
          title: language === 'sr' ? '24/7 sigurnosni monitoring' : '24/7 Security Monitoring',
          description: language === 'sr' 
            ? 'Kontinuirani nadzor sigurnosnih događaja putem SIEM'
            : 'Continuous security event monitoring via SIEM',
          status: 'implemented'
        },
        {
          id: 5,
          category: language === 'sr' ? 'Sigurnosna kopija' : 'Backup',
          title: language === 'sr' ? 'Automatske sigurnosne kopije' : 'Automated Backups',
          description: language === 'sr' 
            ? 'Dnevne enkriptovane sigurnosne kopije sa geo-redundancijom'
            : 'Daily encrypted backups with geo-redundancy',
          status: 'implemented'
        },
        {
          id: 6,
          category: language === 'sr' ? 'Oporavak' : 'Recovery',
          title: language === 'sr' ? 'Plan oporavka od katastrofe' : 'Disaster Recovery Plan',
          description: language === 'sr' 
            ? 'Dokumentovan i testiran DR plan sa RTO < 4h'
            : 'Documented and tested DR plan with RTO < 4h',
          status: 'implemented'
        }
      ]);
      
    } catch (error) {
      console.error('Failed to load trust center data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };
  
  const handleDocumentClick = (doc: ComplianceDocument) => {
    if (doc.requires_nda) {
      setSelectedDocument(doc);
      setShowNDAModal(true);
    } else {
      // Download directly
      window.open(`/documents/${doc.id}`, '_blank');
    }
  };
  
  const handleNDASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ndaForm.agreed) return;
    
    try {
      // Submit NDA request
      await new Promise(resolve => setTimeout(resolve, 1000));
      setNdaSubmitted(true);
    } catch (error) {
      console.error('NDA submission failed:', error);
    }
  };
  
  const getCategoryLabel = (category: string) => {
    const labels: Record<string, { sr: string; en: string }> = {
      all: { sr: 'Sve', en: 'All' },
      certification: { sr: 'Sertifikati', en: 'Certifications' },
      policy: { sr: 'Politike', en: 'Policies' },
      report: { sr: 'Izvještaji', en: 'Reports' },
      attestation: { sr: 'Atestacije', en: 'Attestations' },
      questionnaire: { sr: 'Upitnici', en: 'Questionnaires' }
    };
    return labels[category]?.[language] || category;
  };
  
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'certification': return Award;
      case 'policy': return FileText;
      case 'report': return FileCheck;
      case 'attestation': return BadgeCheck;
      case 'questionnaire': return Bookmark;
      default: return FileText;
    }
  };
  
  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = activeCategory === 'all' || doc.category === activeCategory;
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  
  const categories = ['all', 'certification', 'policy', 'report', 'attestation', 'questionnaire'];
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-slate-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-600 dark:text-gray-400">
            {language === 'sr' ? 'Učitavanje...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-slate-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-xl text-gray-800 dark:text-white">
                {organization?.name || 'Trust Center'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {language === 'sr' ? 'Centar povjerenja' : 'Trust Center'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLanguage(language === 'sr' ? 'en' : 'sr')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={language === 'sr' ? 'Switch to English' : 'Prebaci na srpski'}
            >
              <Globe className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>
      </header>
      
      {/* Hero section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-700" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNMjAgMjBsMjAgMjBIMHYtNDBsMjAgMjB6IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9Ii4wNSIvPjwvZz48L3N2Zz4=')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm mb-6">
              <CheckCircle className="w-4 h-4" />
              <span>{language === 'sr' ? 'Verifikovana sigurnost' : 'Verified Security'}</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {language === 'sr' 
                ? 'Naša posvećenost sigurnosti i usklađenosti'
                : 'Our Commitment to Security & Compliance'}
            </h2>
            
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
              {language === 'sr' 
                ? 'Pregledajte naše sertifikate, politike i sigurnosne kontrole. Transparentnost je temelj povjerenja.'
                : 'Review our certifications, policies, and security controls. Transparency is the foundation of trust.'}
            </p>
            
            {/* Quick stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{certifications.filter(c => c.status === 'active').length}</p>
                <p className="text-sm text-white/70">{language === 'sr' ? 'Aktivnih sertifikata' : 'Active Certifications'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{documents.filter(d => d.is_public).length}</p>
                <p className="text-sm text-white/70">{language === 'sr' ? 'Javnih dokumenata' : 'Public Documents'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{securityControls.filter(s => s.status === 'implemented').length}</p>
                <p className="text-sm text-white/70">{language === 'sr' ? 'Implementiranih kontrola' : 'Implemented Controls'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-3xl font-bold text-white">99.9%</p>
                <p className="text-sm text-white/70">{language === 'sr' ? 'Uptime SLA' : 'Uptime SLA'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Certifications Section */}
        <section className="mb-8">
          <button
            onClick={() => toggleSection('certifications')}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800 dark:text-white">
                  {language === 'sr' ? 'Sertifikati i atestacije' : 'Certifications & Attestations'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {language === 'sr' 
                    ? `${certifications.filter(c => c.status === 'active').length} aktivnih sertifikata`
                    : `${certifications.filter(c => c.status === 'active').length} active certifications`}
                </p>
              </div>
            </div>
            {expandedSections.has('certifications') ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.has('certifications') && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {certifications.map(cert => (
                <div
                  key={cert.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      cert.status === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {cert.status === 'active' 
                        ? (language === 'sr' ? 'Aktivan' : 'Active')
                        : cert.status}
                    </span>
                  </div>
                  
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-1">{cert.name}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {language === 'sr' ? 'Izdao' : 'Issued by'}: {cert.issuer}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(cert.issue_date).toLocaleDateString(language === 'sr' ? 'sr-Latn' : 'en')}
                    </span>
                    {cert.expiry_date && (
                      <span>
                        {language === 'sr' ? 'Do' : 'Until'}: {new Date(cert.expiry_date).toLocaleDateString(language === 'sr' ? 'sr-Latn' : 'en')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        
        {/* Documents Section */}
        <section className="mb-8">
          <button
            onClick={() => toggleSection('documents')}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800 dark:text-white">
                  {language === 'sr' ? 'Dokumentacija' : 'Documentation'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {language === 'sr' 
                    ? `${documents.length} dostupnih dokumenata`
                    : `${documents.length} available documents`}
                </p>
              </div>
            </div>
            {expandedSections.has('documents') ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.has('documents') && (
            <>
              {/* Search and filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'sr' ? 'Pretraži dokumenta...' : 'Search documents...'}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        activeCategory === cat
                          ? 'bg-blue-500 text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {getCategoryLabel(cat)}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Documents grid */}
              <div className="grid gap-4 md:grid-cols-2">
                {filteredDocuments.map(doc => {
                  const CategoryIcon = getCategoryIcon(doc.category);
                  
                  return (
                    <div
                      key={doc.id}
                      onClick={() => handleDocumentClick(doc)}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all cursor-pointer group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CategoryIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                              {doc.title}
                            </h4>
                            {doc.requires_nda && (
                              <span className="flex-shrink-0" title={language === 'sr' ? 'Potreban NDA' : 'NDA Required'}>
                                <Lock className="w-4 h-4 text-amber-500" />
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                            {doc.description}
                          </p>
                          
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {doc.file_type.toUpperCase()}
                            </span>
                            <span>{doc.file_size}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(doc.last_updated).toLocaleDateString(language === 'sr' ? 'sr-Latn' : 'en')}
                            </span>
                            {doc.framework && (
                              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                                {doc.framework}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <Download className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {filteredDocuments.length === 0 && (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl">
                  <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    {language === 'sr' ? 'Nema dokumenata za prikaz' : 'No documents to display'}
                  </p>
                </div>
              )}
            </>
          )}
        </section>
        
        {/* Security Controls Section */}
        <section className="mb-8">
          <button
            onClick={() => toggleSection('controls')}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800 dark:text-white">
                  {language === 'sr' ? 'Sigurnosne kontrole' : 'Security Controls'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Pregled naših implementiranih sigurnosnih mjera'
                    : 'Overview of our implemented security measures'}
                </p>
              </div>
            </div>
            {expandedSections.has('controls') ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.has('controls') && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {securityControls.map(control => (
                <div
                  key={control.id}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">
                      {language === 'sr' ? 'Implementirano' : 'Implemented'}
                    </span>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{control.category}</p>
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-2">{control.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{control.description}</p>
                </div>
              ))}
            </div>
          )}
        </section>
        
        {/* Contact Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-lg text-gray-800 dark:text-white mb-4">
            {language === 'sr' ? 'Kontaktirajte nas za pitanja o sigurnosti' : 'Contact Us for Security Inquiries'}
          </h3>
          
          <div className="grid gap-4 md:grid-cols-2">
            <a
              href={`mailto:${organization?.security_email}`}
              className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
            >
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-gray-800 dark:text-white">
                  {language === 'sr' ? 'Sigurnosni tim' : 'Security Team'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{organization?.security_email}</p>
              </div>
            </a>
            
            {organization?.dpo_email && (
              <a
                href={`mailto:${organization.dpo_email}`}
                className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
              >
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <User className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">
                    {language === 'sr' ? 'Službenik za zaštitu podataka (DPO)' : 'Data Protection Officer (DPO)'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{organization.dpo_email}</p>
                </div>
              </a>
            )}
          </div>
        </section>
      </main>
      
      {/* NDA Modal */}
      {showNDAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-white">
                    {language === 'sr' ? 'NDA zahtjev' : 'NDA Request'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedDocument?.title}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowNDAModal(false);
                  setNdaSubmitted(false);
                  setNdaForm({ name: '', email: '', company: '', agreed: false });
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              {!ndaSubmitted ? (
                <>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 mb-6">
                    <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {language === 'sr' 
                        ? 'Ovaj dokument zahtijeva potpisani NDA. Popunite formu ispod da zatražite pristup.'
                        : 'This document requires a signed NDA. Fill out the form below to request access.'}
                    </p>
                  </div>
                  
                  <form onSubmit={handleNDASubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {language === 'sr' ? 'Ime i prezime' : 'Full Name'}
                      </label>
                      <input
                        type="text"
                        value={ndaForm.name}
                        onChange={(e) => setNdaForm({ ...ndaForm, name: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {language === 'sr' ? 'Poslovni email' : 'Business Email'}
                      </label>
                      <input
                        type="email"
                        value={ndaForm.email}
                        onChange={(e) => setNdaForm({ ...ndaForm, email: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {language === 'sr' ? 'Kompanija' : 'Company'}
                      </label>
                      <input
                        type="text"
                        value={ndaForm.company}
                        onChange={(e) => setNdaForm({ ...ndaForm, company: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="nda-agree"
                        checked={ndaForm.agreed}
                        onChange={(e) => setNdaForm({ ...ndaForm, agreed: e.target.checked })}
                        className="mt-1"
                        required
                      />
                      <label htmlFor="nda-agree" className="text-sm text-gray-600 dark:text-gray-400">
                        {language === 'sr' 
                          ? 'Slažem se da ovaj dokument koristim isključivo za procjenu vaših sigurnosnih praksi i da neću dijeliti njegov sadržaj bez pisane saglasnosti.'
                          : 'I agree to use this document solely for evaluating your security practices and will not share its contents without written consent.'}
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={!ndaForm.agreed}
                      className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Send className="w-5 h-5" />
                      {language === 'sr' ? 'Pošalji zahtjev' : 'Submit Request'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    {language === 'sr' ? 'Zahtjev poslan' : 'Request Submitted'}
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    {language === 'sr' 
                      ? 'Naš tim će pregledati vaš zahtjev i kontaktirati vas putem emaila u roku od 24-48 sati.'
                      : 'Our team will review your request and contact you via email within 24-48 hours.'}
                  </p>
                  <button
                    onClick={() => {
                      setShowNDAModal(false);
                      setNdaSubmitted(false);
                      setNdaForm({ name: '', email: '', company: '', agreed: false });
                    }}
                    className="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {language === 'sr' ? 'Zatvori' : 'Close'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="py-8 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
        <p className="mb-2">© 2026 {organization?.name}. {language === 'sr' ? 'Sva prava zadržana.' : 'All rights reserved.'}</p>
        <p className="text-xs">{organization?.headquarters} • {organization?.industry}</p>
      </footer>
    </div>
  );
};

export default PublicTrustCenter;
